/**
 * ClientApp/src/apps/components/mapViewers/tests/PieMap/utils/pieMapUtils.js
 *
 * Self-contained utilities for the PieMap sandbox.
 *
 * FeatureLayer tests:
 *   Test A — simple count cluster
 *   Test D — smart pie cluster
 *
 * MapImageLayer:
 *   Test F
 */

import FeatureLayer from "@arcgis/core/layers/FeatureLayer.js";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer.js";

import {
  applySmartClusterPieRenderer,
} from "./clusterPieSmartMapping";

const getServicesArray = (services) =>
  Array.isArray(services)
    ? services
    : Object.values(services);

/* -------------------------------------------------------------
   FEATURE LAYERS — Tests A + D
------------------------------------------------------------- */

export function buildPieMapLayers(map, services) {
  if (!map || !services) return [];

  return getServicesArray(services)
    .filter((service) => !service.isExcluded)
    .map((service) => {
      const {
        isExcluded,
        smartClusterPie,
        ...layerProperties
      } = service;

      const layer = new FeatureLayer({
        ...layerProperties,

        /*
         * Test A already has a static featureReduction.
         *
         * Test D gets featureReduction asynchronously after
         * createRendererForClustering() finishes.
         */
        featureReduction: smartClusterPie
          ? undefined
          : layerProperties.featureReduction,
      });

      map.add(layer);

      return {
        layer,
        service,
      };
    });
}

/**
 * Builds FeatureLayers and initializes any async smart-pie configuration.
 */
export function initPieMapLayers(map, view, services) {
  const built = buildPieMapLayers(map, services);

  built.forEach(({ layer, service }) => {
    if (!service.smartClusterPie) {
      return;
    }

    applySmartClusterPieRenderer(
      layer,
      view,
      service.smartClusterPie
    ).catch((error) => {
      console.error(
        `[pieMapUtils] Smart cluster pie failed [${service.title}]:`,
        error
      );
    });
  });

  return built;
}

/* -------------------------------------------------------------
   MAP IMAGE LAYERS — Test F
------------------------------------------------------------- */

export function buildPieMapImageLayers(map, services) {
  if (!map || !services) return [];

  return getServicesArray(services)
    .filter((service) => !service.isExcluded)
    .map((service) => {
      const {
        isExcluded,
        url,
        title,
        opacity,
        visible,
        sublayers,
        ...otherProps
      } = service;

      const layer = new MapImageLayer({
        url,
        title,
        opacity: opacity ?? 1,
        visible: visible ?? true,
        sublayers: sublayers || [],
        ...otherProps,
      });

      map.add(layer);

      return layer;
    });
}